import * as Sentry from "@sentry/react";
import {
  sendPasswordResetEmail,
  signInWithCustomToken,
  signInWithEmailAndPassword,
  updatePassword as updateFirebasePassword,
  UserCredential,
} from "firebase/auth";
import { doc, DocumentSnapshot, onSnapshot } from "firebase/firestore";
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { clearPendingReferralCode } from "src/features/referrals/utils/pendingReferralCode";
import registerStudentCallable from "src/features/users/services/registerStudent";
import { auth, db, functions } from "src/firebase";
import cleanFirestoreDoc from "src/firebase/cleanFirestoreDoc";
import FirestoreClient from "src/firebase/FirestoreClient";
import { identify, resetIdentity } from "src/hooks/useInstrumentation";
import { SignupFormData } from "src/pages/SignupScreen/SignupScreen";
import { getGoogleClickIds } from "src/shared/utils/getGoogleClickIds";
import { getMetaClickIds } from "src/shared/utils/getMetaClickIds";
import { getTiktokClickIds } from "src/shared/utils/getTiktokClickIds";
import {
  captureUtmAttribution,
  clearPendingUtmAttribution,
} from "src/shared/utils/getUtmAttribution";
import { Partner } from "src/types/Partner";
import { UserAccount, UserType } from "src/types/User";
import { VocationId } from "src/types/Vocation";
import { normalizeStr } from "src/utils";

type Session = FirestoreClient;

export type CreateUserInput = Omit<
  SignupFormData,
  "passwordConfirmation" | "termsOfService" | "dateOfBirth"
> &
  Required<Pick<SignupFormData, "state">> & {
    dateOfBirth: string; // whether the user is the admin of an organization
    organizationId?: string;
    cohortId?: string;
    /**
     * Referring partner's uid, resolved at signup from a `referralCodes` lookup
     * (or the legacy `partners` fallback). Only the uid crosses this boundary —
     * nothing here needs the rest of the partner document.
     */
    referredByPartnerId?: string;
    /**
     * Referring student's uid, resolved from the same lookup. Kept apart from
     * `referredByPartnerId` so student referrals never trip the partner bonus
     * tracking and Customer.io partner sync.
     */
    referredByUserId?: string;
    /** Validated upstream against a live vocations/{id} doc; absent = CDL. */
    vocationId?: VocationId;
    /**
     * Proof of the SMS code from `requestSignupPhoneVerificationCode`.
     * Required for real phone numbers unless that callable answered
     * `not_required` (shared placeholder numbers or a pre-release cohort
     * invite link).
     */
    phoneVerification?: { code: string; requestId: string };
  };

type AuthContexTType = {
  session: Session | null;
  isLoggedIn: boolean;
  isLoading: boolean;
  // need to add isLoading definition that also takes into account users that do not have accounts
  login: (email: string, password: string) => Promise<UserCredential>;
  /**
   * Signs in with a server-minted Firebase custom token — used by the
   * passwordless login-code flow (`verifyLoginCode`), mirroring how
   * registration signs in without re-sending the password.
   */
  loginWithToken: (token: string) => Promise<UserCredential>;
  registerStudent: (data: CreateUserInput) => Promise<boolean>;

  logout: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  updatePassword: (password: string) => Promise<void> | null;
};

const AuthContext = React.createContext<AuthContexTType>({} as AuthContexTType);

// Separate context for liveUser so that user-document changes only re-render
// components that actually read liveUser (useCurrentUser), not every AuthContext
// consumer (SessionBoundary, useSession, useAdmin, usePartner, etc.).
const LiveUserContext = React.createContext<UserAccount | null>(null);

export function useAuth() {
  return useContext(AuthContext);
}

export function useLiveUser(): UserAccount | null {
  return useContext(LiveUserContext);
}

export function useSession(): Session {
  const { session } = useAuth();

  if (!session) throw new Error("No auth session");

  return useMemo(() => session, [session]);
}

export function AuthProvider({
  children,
}: {
  children: React.ReactNode | React.ReactNode[];
}) {
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setLoading] = useState(true);
  const [liveUser, setLiveUser] = useState<UserAccount | null>(null);
  const prevUserRef = useRef<UserAccount | null>(null);
  const navigate = useNavigate();

  // Single shared onSnapshot listener for the user document.
  // This replaces per-component listeners in useCurrentUser(), eliminating
  // duplicate Firestore connections and cascading re-renders.
  useEffect(() => {
    if (!session?.metadata || session.metadata.type !== UserType.User) {
      setLiveUser(null);
      prevUserRef.current = null;
      return undefined;
    }

    const userDoc = doc(db, "users", session.credentials.uid);
    const unsubscribe = onSnapshot(userDoc, (snapshot: DocumentSnapshot) => {
      const updatedUser = cleanFirestoreDoc(snapshot);
      if (!updatedUser) return;

      // Deep-compare to prevent unnecessary re-renders.
      // Firestore's onSnapshot fires on every server ack/metadata change,
      // even when document data is identical. JSON.stringify is reliable here
      // because: (1) Firestore keys are consistently ordered, (2) user doc
      // is small (~20 fields), (3) Firestore doesn't store undefined values.
      if (JSON.stringify(updatedUser) !== JSON.stringify(prevUserRef.current)) {
        prevUserRef.current = updatedUser as UserAccount;
        setLiveUser(updatedUser as UserAccount);
        // Update session metadata for backward compatibility
        session.updateUserAccount(updatedUser as UserAccount);
      }
    });

    // Initialize liveUser from session metadata
    const initialUser = session.metadata as UserAccount;
    prevUserRef.current = initialUser;
    setLiveUser(initialUser);

    return unsubscribe;
  }, [session]);

  // load session
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(
      async (user) => {
        // NOTE: firebase event listeners are handy here because
        // they will catch sign-in, logout and register events on our behalf
        // will need to redo this later
        setLoading(true);
        if (user) {
          await FirestoreClient.create(user)
            .then((res) => {
              setSession(res);
              Sentry.setUser({ email: user.email || user.uid, uid: user.uid });
            })
            // eslint-disable-next-line no-alert
            .catch((err) => window.alert(err.message));
        }
        setLoading(false);
      },
      (err) => {
        setLoading(false);
        throw err;
      }
    );
    return unsubscribe;
  }, []);

  useEffect(() => {
    if (!session || !session.metadata) return;
    identify({ userId: session.metadata.uid });
  }, [session]);

  const value = useMemo(
    (): AuthContexTType => ({
      session,
      // whether the session can be retrieved
      isLoggedIn: !!session,
      isLoading,
      login: (email: string, password: string) =>
        signInWithEmailAndPassword(auth, normalizeStr(email), password),
      loginWithToken: (token: string) => signInWithCustomToken(auth, token),
      registerStudent: async ({
        password,
        referredByPartnerId,
        referredByUserId,
        dateOfBirth,
        ...input
      }) => {
        // Capture Meta + Google Ads click identifiers and standard UTM campaign
        // attribution from browser cookies/URL. This is the only part of
        // registration that must stay client-side, since the cookies and
        // landing URL aren't readable server-side. All account creation and
        // tracking now happens on the server via the registerStudent callable.
        const metaClickIds = getMetaClickIds();
        const hasMetaClickIds = Object.keys(metaClickIds).length > 0;
        const googleClickIds = getGoogleClickIds();
        const hasGoogleClickIds = Object.keys(googleClickIds).length > 0;
        const tiktokClickIds = getTiktokClickIds();
        const hasTiktokClickIds = Object.keys(tiktokClickIds).length > 0;
        const utmAttribution = captureUtmAttribution();
        const hasUtmAttribution = !!utmAttribution;
        const attributionCapturedAt = new Date().toISOString();

        // Server-side: create the Firebase Auth user + users/{uid} doc, enforce
        // unique email/phone, and fire the server-side analytics + Meta
        // CompleteRegistration conversion (via the on-create trigger). Returns a
        // custom token so we can sign in without re-sending the password.
        const { customToken } = await registerStudentCallable(
          {
            firstName: input.firstName,
            middleName: input.middleName,
            lastName: input.lastName,
            email: input.email,
            phone: input.phone,
            phoneVerification: input.phoneVerification,
            state: input.state,
            city: input.city,
            password,
            dateOfBirth,
            referralSource: input.referralSource,
            incarcerationStatus: input.incarcerationStatus,
            doesNotHaveEmail: input.doesNotHaveEmail,
            otherCity: input.otherCity,
            organizationId: input.organizationId,
            cohortId: input.cohortId,
            vocationId: input.vocationId,
            referredByPartnerId,
            // Student referral (kept apart from referredByPartnerId). When
            // undefined it's dropped from the callable's JSON payload, so an
            // unattributed signup leaves the field off the user doc entirely.
            referredByUserId,
            ...((hasMetaClickIds ||
              hasGoogleClickIds ||
              hasTiktokClickIds ||
              hasUtmAttribution) && {
              marketing: {
                ...(hasMetaClickIds && {
                  meta: {
                    ...metaClickIds,
                    capturedAt: attributionCapturedAt,
                  },
                }),
                ...(hasGoogleClickIds && {
                  google: {
                    ...googleClickIds,
                    capturedAt: attributionCapturedAt,
                  },
                }),
                ...(hasTiktokClickIds && {
                  tiktok: {
                    ...tiktokClickIds,
                    capturedAt: attributionCapturedAt,
                  },
                }),
                ...(hasUtmAttribution && {
                  utm: utmAttribution,
                }),
              },
            }),
          },
          functions
        );

        await signInWithCustomToken(auth, customToken);
        return true;
      },

      logout: async () => {
        // Determine redirect path based on account type
        let redirectPath = "/login"; // Default for User, Admin

        if (session?.metadata?.type === UserType.Partner) {
          const partner = session.metadata as Partner;
          // If partner has bonus.version, they're an ambassador
          redirectPath = partner.bonus?.version
            ? "/ambassadors/login"
            : "/partners/login";
        }

        // Clear analytics identity before logout to prevent data leakage
        resetIdentity();
        // Same reasoning for referral attribution: on a shared facility or
        // library machine, logout is the handoff to the next person, and they
        // must not inherit a referral code left behind by this session.
        clearPendingReferralCode();
        clearPendingUtmAttribution();

        setSession(null);
        await auth.signOut();
        navigate(redirectPath);
      },
      resetPassword: (email: string) =>
        sendPasswordResetEmail(auth, normalizeStr(email)),
      updatePassword: (password: string) =>
        session &&
        updateFirebasePassword(session.credentials, normalizeStr(password)),
    }),
    [session, isLoading, navigate]
  );

  return (
    <AuthContext.Provider value={value}>
      <LiveUserContext.Provider value={liveUser}>
        {children}
      </LiveUserContext.Provider>
    </AuthContext.Provider>
  );
}
